import { useMemo } from 'react'

import { useQuery } from '@apollo/client'

import { Select, SelectProps } from '../shared-components/BUI/Select'
import { Text } from '../shared-components/BUI/Text'
import { Flexbox } from '../shared-components/Flexbox'

import { GET_USER_WORKER_ID } from '@/modules/user/graphQL'
import { GET_WORKERS_NAME_WORKER } from '@/modules/workers/graphQL'
import { getWorkerName } from '@/utils/index'

import {
  UserWorkerIdForPublicRoutesUiQuery,
  UserWorkerIdForPublicRoutesUiQueryVariables,
  WorkersNameWorkerUiQuery,
  WorkersNameWorkerUiQueryVariables,
} from 'types/api.graphql'

export type WorkerInputValue = SelectProps['value']

type Props = {
  projectId: string
  value?: WorkerInputValue
  onChange?: (value: SelectProps['value']) => void
  multiple?: boolean
  hideCurrentWorker?: boolean
}

export const WorkerInputWorker: React.FC<Props> = ({
  projectId,
  value,
  onChange,
  multiple,
  hideCurrentWorker = false,
}) => {
  const { data } = useQuery<
    WorkersNameWorkerUiQuery,
    WorkersNameWorkerUiQueryVariables
  >(GET_WORKERS_NAME_WORKER, {
    variables: {
      projectId,
    },
    skip: !projectId,
  })

  const { data: userData } = useQuery<
    UserWorkerIdForPublicRoutesUiQuery,
    UserWorkerIdForPublicRoutesUiQueryVariables
  >(GET_USER_WORKER_ID)

  const workerOptions = useMemo(() => {
    if (!data?.workers_worker) return
    return data.workers_worker
      .filter(
        (worker) =>
          !(hideCurrentWorker && worker.id === userData.user.worker.id)
      )
      .map((w) => ({
        value: w.id,
        label: (
          <Flexbox flexDirection="column">
            {getWorkerName(w)}
            <Text variant="caption01" secondary="_500">
              {w.trade}
            </Text>
          </Flexbox>
        ),
        searchableText: `${getWorkerName(w)} ${w.trade}`.toLowerCase(),
      }))
  }, [data, hideCurrentWorker, userData?.user.worker.id])

  return (
    <>
      <Select
        allowClear
        mode={multiple ? 'multiple' : undefined}
        value={value}
        showSearch
        optionFilterProp="searchableText"
        filterOption={(input, option) =>
          option.searchableText.includes(input.toLowerCase())
        }
        onChange={onChange}
        placeholder="Search or scroll to select a worker..."
        options={workerOptions}
        dropdownRender={(menu) => menu}
      />
    </>
  )
}
